<template>
  <v-container fluid class="login-page pa-0">
    <div class="login-backdrop" aria-hidden="true">
      <img :src="background" alt="" class="login-bg-img" />
      <div class="login-bg-glow" />
    </div>

    <div class="login-center">
      <v-card class="login-card" elevation="0" rounded="xl">
        <header class="card-brand">
          <img :src="logo" alt="" class="brand-logo" />
          <h1 class="brand-title">
            <span class="brand-wall">Wall</span>
            <span class="brand-labs">Labs</span>
          </h1>
        </header>

        <div class="welcome-block">
          <v-icon
            icon="mdi-star-four-points"
            size="18"
            color="#a855f7"
            class="welcome-star"
          />
          <h2 class="welcome-title">{{ welcomeTitle }}</h2>
          <p class="welcome-sub">{{ welcomeSub }}</p>
        </div>

        <v-form
          class="login-form"
          @submit.prevent="forgotPasswordMode ? onForgotSubmit() : login()"
        >
          <v-text-field
            v-model="username"
            label="Email"
            type="email"
            autocomplete="username"
            variant="outlined"
            density="comfortable"
            rounded="xl"
            hide-details="auto"
            bg-color="#ffffff"
            prepend-inner-icon="mdi-email-outline"
            class="login-field"
            :readonly="forgotPasswordMode && forgotStep === 2"
          />

          <template v-if="!forgotPasswordMode">
            <v-text-field
              v-model="password"
              label="Password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              variant="outlined"
              density="comfortable"
              rounded="xl"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-inner-icon="mdi-lock-outline"
              :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              class="login-field mt-3"
              @click:append-inner="showPassword = !showPassword"
            />

            <div v-if="!needsNewPassword" class="forgot-row">
              <button
                type="button"
                class="forgot-link"
                @click="startForgotPassword"
              >
                Forgot password?
              </button>
            </div>
          </template>

          <template v-else-if="forgotStep === 2">
            <v-text-field
              v-model="resetCode"
              label="Verification code"
              autocomplete="one-time-code"
              variant="outlined"
              density="comfortable"
              rounded="xl"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-inner-icon="mdi-numeric"
              class="login-field mt-3"
            />
            <v-text-field
              v-model="newPassword"
              label="New password"
              :type="showNewPassword ? 'text' : 'password'"
              autocomplete="new-password"
              variant="outlined"
              density="comfortable"
              rounded="xl"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-inner-icon="mdi-lock-plus-outline"
              :append-inner-icon="showNewPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              class="login-field mt-3"
              @click:append-inner="showNewPassword = !showNewPassword"
            />
            <v-text-field
              v-model="confirmNewPassword"
              label="Confirm new password"
              :type="showConfirmPassword ? 'text' : 'password'"
              autocomplete="new-password"
              variant="outlined"
              density="comfortable"
              rounded="xl"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-inner-icon="mdi-lock-check-outline"
              :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              class="login-field mt-3"
              @click:append-inner="showConfirmPassword = !showConfirmPassword"
            />
          </template>

          <template v-if="needsNewPassword">
            <v-alert
              type="warning"
              variant="tonal"
              class="mt-4 mb-2"
              density="comfortable"
              rounded="lg"
            >
              New password required. Enter and confirm your new password.
            </v-alert>
            <v-text-field
              v-model="newPassword"
              label="New password"
              type="password"
              variant="outlined"
              density="comfortable"
              rounded="xl"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-inner-icon="mdi-lock-plus-outline"
              class="login-field mt-2"
            />
            <v-text-field
              v-model="confirmNewPassword"
              label="Confirm new password"
              type="password"
              variant="outlined"
              density="comfortable"
              rounded="xl"
              hide-details="auto"
              bg-color="#ffffff"
              prepend-inner-icon="mdi-lock-check-outline"
              class="login-field mt-3"
            />
          </template>

          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="mt-4"
            density="compact"
            rounded="lg"
          >
            {{ errorMessage }}
          </v-alert>
          <v-alert
            v-if="successMessage"
            type="success"
            variant="tonal"
            class="mt-4"
            density="compact"
            rounded="lg"
          >
            {{ successMessage }}
          </v-alert>

          <v-btn
            v-if="!forgotPasswordMode"
            type="submit"
            block
            size="x-large"
            rounded="xl"
            class="mt-6 sign-in-btn"
            :loading="loading"
            :disabled="needsNewPassword"
          >
            Sign In
            <template #append>
              <v-icon icon="mdi-arrow-right" size="22" />
            </template>
          </v-btn>

          <v-btn
            v-if="forgotPasswordMode"
            type="submit"
            block
            size="x-large"
            rounded="xl"
            class="mt-6 sign-in-btn"
            :loading="loading"
          >
            {{ forgotStep === 1 ? 'Send reset code' : 'Reset password' }}
          </v-btn>

          <v-btn
            v-if="forgotPasswordMode"
            type="button"
            variant="text"
            block
            class="mt-2 back-link-btn"
            :disabled="loading"
            @click="cancelForgotPassword"
          >
            Back to sign in
          </v-btn>

          <v-btn
            v-if="needsNewPassword"
            color="primary"
            block
            class="mt-3"
            size="large"
            variant="tonal"
            rounded="xl"
            :loading="loading"
            @click="setNewPassword"
          >
            Set new password
          </v-btn>
        </v-form>

        <footer class="card-footer">
          <p class="copyright">
            © {{ currentYear }} Wall Labs. All rights reserved.
          </p>
          <router-link to="/privacy-policy" class="privacy-link">
            <v-icon icon="mdi-shield-account-outline" size="18" />
            Privacy Policy
          </router-link>
        </footer>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import logo from '@/assets/wall-labs-logo.png'
import background from '@/assets/login-background.png'
import {
  loginWithCognito,
  completeNewPasswordChallenge,
  requestPasswordReset,
  confirmPasswordReset
} from '@/services/cognitoAuth'

export default {
  name: 'LoginPage',
  data() {
    return {
      logo,
      background,
      username: '',
      password: '',
      newPassword: '',
      confirmNewPassword: '',
      challengeSession: '',
      needsNewPassword: false,
      forgotPasswordMode: false,
      forgotStep: 1,
      resetCode: '',
      loading: false,
      errorMessage: '',
      successMessage: '',
      showPassword: false,
      showNewPassword: false,
      showConfirmPassword: false
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    welcomeTitle() {
      if (this.forgotPasswordMode) {
        return this.forgotStep === 1 ? 'Reset password' : 'Enter new password'
      }
      return 'Welcome back'
    },
    welcomeSub() {
      if (this.forgotPasswordMode) {
        return this.forgotStep === 1
          ? 'We will email you a verification code'
          : 'Enter the code from your email and choose a new password'
      }
      return 'Sign in to continue to your account'
    }
  },
  methods: {
    startForgotPassword() {
      this.forgotPasswordMode = true
      this.forgotStep = 1
      this.resetCode = ''
      this.newPassword = ''
      this.confirmNewPassword = ''
      this.errorMessage = ''
      this.successMessage = ''
    },
    cancelForgotPassword() {
      this.forgotPasswordMode = false
      this.forgotStep = 1
      this.resetCode = ''
      this.newPassword = ''
      this.confirmNewPassword = ''
      this.errorMessage = ''
      this.successMessage = ''
    },
    onForgotSubmit() {
      if (this.forgotStep === 1) {
        this.sendResetCode()
      } else {
        this.resetPassword()
      }
    },
    async sendResetCode() {
      this.errorMessage = ''
      this.successMessage = ''

      const email = String(this.username || '').trim()
      if (!email) {
        this.errorMessage = 'Please enter your email address.'
        return
      }

      this.loading = true
      try {
        const result = await requestPasswordReset(email)
        const destination =
          result.codeDeliveryDetails?.Destination || 'your email'
        this.forgotStep = 2
        this.successMessage = `Verification code sent to ${destination}.`
      } catch (error) {
        this.errorMessage = error.message || 'Failed to send reset code.'
      } finally {
        this.loading = false
      }
    },
    async resetPassword() {
      this.errorMessage = ''
      this.successMessage = ''

      const email = String(this.username || '').trim()
      const code = String(this.resetCode || '').trim()

      if (!email) {
        this.errorMessage = 'Please enter your email address.'
        return
      }
      if (!code) {
        this.errorMessage = 'Please enter the verification code.'
        return
      }
      if (!this.newPassword || !this.confirmNewPassword) {
        this.errorMessage = 'Please enter and confirm your new password.'
        return
      }
      if (this.newPassword !== this.confirmNewPassword) {
        this.errorMessage = 'New password and confirmation do not match.'
        return
      }

      this.loading = true
      try {
        await confirmPasswordReset({
          username: email,
          confirmationCode: code,
          newPassword: this.newPassword
        })
        this.password = ''
        this.resetCode = ''
        this.newPassword = ''
        this.confirmNewPassword = ''
        this.forgotPasswordMode = false
        this.forgotStep = 1
        this.successMessage =
          'Password reset successfully. Sign in with your new password.'
      } catch (error) {
        this.errorMessage = error.message || 'Failed to reset password.'
      } finally {
        this.loading = false
      }
    },
    async login() {
      this.errorMessage = ''
      this.successMessage = ''
      this.loading = true
      try {
        const result = await loginWithCognito(this.username, this.password)
        if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.needsNewPassword = true
          this.challengeSession = result.session || ''
          this.successMessage = 'Set a new password to continue.'
        } else if (result.authResult) {
          this.needsNewPassword = false
          this.challengeSession = ''
          this.$router.push('/Landing')
        } else {
          this.successMessage = 'Login request completed.'
        }
      } catch (error) {
        this.errorMessage = error.message || 'Login failed.'
      } finally {
        this.loading = false
      }
    },
    async setNewPassword() {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.newPassword || !this.confirmNewPassword) {
        this.errorMessage = 'Please enter and confirm your new password.'
        return
      }
      if (this.newPassword !== this.confirmNewPassword) {
        this.errorMessage = 'New password and confirmation do not match.'
        return
      }

      this.loading = true
      try {
        const result = await completeNewPasswordChallenge({
          username: this.username,
          newPassword: this.newPassword,
          session: this.challengeSession
        })

        if (result.authResult) {
          this.needsNewPassword = false
          this.challengeSession = ''
          this.newPassword = ''
          this.confirmNewPassword = ''
          this.$router.push('/')
        } else if (result.challengeName) {
          this.errorMessage = `Additional challenge required: ${result.challengeName}`
        } else {
          this.$router.push('/')
        }
      } catch (error) {
        this.errorMessage = error.message || 'Failed to set new password.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  overflow: hidden;
}

.login-backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: #060b1a;
}

.login-bg-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.login-bg-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 55% 45% at 50% 48%,
    rgba(99, 102, 241, 0.22) 0%,
    transparent 65%
  );
  pointer-events: none;
}

.login-center {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
}

.login-card {
  padding: 36px 40px 28px;
  background: #ffffff !important;
  border: none;
  border-radius: 24px !important;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06),
    0 24px 64px rgba(0, 0, 0, 0.45),
    0 0 80px rgba(99, 102, 241, 0.15) !important;
}

.card-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
}

.brand-logo {
  width: 52px;
  height: 52px;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
}

.brand-wall {
  background: linear-gradient(135deg, #a855f7 0%, #6366f1 50%, #3b82f6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-labs {
  color: #1e293b;
  margin-left: 0.15em;
}

.welcome-block {
  margin-bottom: 26px;
}

.welcome-star {
  display: block;
  margin-bottom: 8px;
}

.welcome-title {
  margin: 0 0 6px;
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.welcome-sub {
  margin: 0;
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.45;
}

.login-form :deep(.login-field .v-field) {
  border-radius: 14px;
  background: #fff;
}

.login-form :deep(.v-field__outline) {
  --v-field-border-opacity: 0.3;
  color: #cbd5e1;
}

.login-form :deep(.v-field__input) {
  color: #0f172a;
}

.sign-in-btn {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 55%, #a855f7 100%) !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 1.0625rem;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow: 0 10px 28px rgba(99, 102, 241, 0.45) !important;
}

.sign-in-btn :deep(.v-btn__content) {
  gap: 8px;
}

.card-footer {
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid #f1f5f9;
  text-align: center;
}

.copyright {
  margin: 0 0 12px;
  font-size: 0.75rem;
  color: #94a3b8;
}

.privacy-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6366f1;
  text-decoration: none;
}

.privacy-link:hover {
  color: #8b5cf6;
}

.forgot-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.forgot-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6366f1;
  cursor: pointer;
  text-decoration: none;
}

.forgot-link:hover {
  color: #8b5cf6;
  text-decoration: underline;
}

.back-link-btn {
  text-transform: none;
  font-weight: 600;
  color: #64748b !important;
}

@media (max-width: 480px) {
  .login-card {
    padding: 28px 24px 22px;
  }

  .welcome-title {
    font-size: 1.5rem;
  }
}
</style>
